// Helper utilities for loop expansion control scanning and limit handling.
// Example: find the next `@if` or `@for` token and enforce loop resource budgets.
import { buildResourceLimitWarning, throwResourceLimitError } from '../../resource-limits'
import { matchIfTokenAt } from './if-parser-branches'
import { matchForTokenAt } from './parser'
import type { ForLoopExpansionOptions } from './types'

// Build one loop resource-limit response according to the selected policy.
export function handleLoopLimitViolation(
  options: ForLoopExpansionOptions,
  line: number,
  message: string,
): void {
  // Check whether the current resource policy is fail-closed.
  if (options.resourcePolicy === 'fail-closed') {
    // Throw immediately in fail-closed mode.
    throwResourceLimitError(message)
  } else {
    // Continue in fail-soft mode and store the warning payload.
    options.warnings.push(buildResourceLimitWarning(line, message))
  }
}

// Resolve the 1-based line number for one source index.
export function countLineAt(source: string, index: number): number {
  let line = 1
  // Start one newline count up to the requested cursor index.
  for (let cursor = 0; cursor < index; cursor += 1) {
    if (source[cursor] === '\n') line += 1
  }
  // Return the computed 1-based line number.
  return line
}

// Build the nearest control-flow token start for `@if` or `@for`.
export function findNextControlFlowStart(source: string, fromIndex: number): number {
  // Start one scan and return the first valid control-flow token boundary.
  for (let index = fromIndex; index < source.length; index += 1) {
    const char = source[index]
    if (char === '@') {
      // Check whether the current `@` token starts an if/for control block.
      if (matchIfTokenAt(source, index) || matchForTokenAt(source, index) > 0) {
        // Return the current index for a matched control-flow token.
        return index
      }
    } else if (char === 'f') {
      // Check whether the current `f` token starts a bare `for` control block.
      if (matchForTokenAt(source, index) > 0) {
        // Return the current index for a matched bare `for` token.
        return index
      }
    }
  }
  // Return `-1` when no control-flow token is found.
  return -1
}
